import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { loginUser } from '../../pages/user/login/services/post/login/login';
import { decodeJWT } from '../../pages/user/login/services/utils/decodedJWT';
import getUserPermissions from '../../services/permissions/getPermissions';
import { LoginResponse, TwoFactorMethod } from '../../pages/user/login/services/post/login/interfaces';
import { JWTPayload } from '../../pages/user/login/services/post/login/models';
import { fetchProfile } from '../../services/profileSidebar/profileSidebar';
import { trackLogin } from '../../utils/gtm/GTMService';
import { Profile } from '../../services/profileSidebar/interface';
import ProfileIncompleteModal from '../../components/ProfileIncompleteModal/ProfileIncompleteModal';
import { verifyTwoFactorLogin } from '../../pages/user/login/services/post/login/twoFactorAuth';
import { tokenStorage } from '../../utils/tokenStorage';

interface AuthContextProps {
  tokens: LoginResponse | null;
  user: JWTPayload | null;
  profile: Profile | null;
  permissions: string[]; // Initialisé à un tableau vide
  isPermissionsLoading: boolean; // État de chargement des permissions
  isProfileLoading: boolean;
  login: (email: string, password: string, remember: boolean, method?: TwoFactorMethod, code?: string) => Promise<void>;
  loginWithTokens: (
    accessToken: string,
    refreshToken: string,
    source?: string,
    userEmail?: string,
  ) => Promise<void>;
  verifyTwoFactor: (method: TwoFactorMethod, code: string) => Promise<void>;
  setGoogleTwoFactorRequired: (email: string, methods: TwoFactorMethod[]) => void;
  isTokensLoading: boolean;
  logout: () => void;
  refreshProfile: () => Promise<void>; // Added: Method to manually refresh global profile state
  twoFactorMethods: TwoFactorMethod[] | [];
  twoFactorEmail: string | null;
  twoFactorRememberMe: boolean;
  clearTwoFactorState: () => void;
}

interface AuthProviderProps {
  children: React.ReactNode;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const location = useLocation();
  const [tokens, setTokens] = useState<LoginResponse | null>(null);
  const [user, setUser] = useState<JWTPayload | null>(null);
  const [profile, setProfile] = useState<any | null>(null);
  const [permissions, setPermissions] = useState<string[]>([]); // Permissions initialisées à un tableau vide
  const [isPermissionsLoading, setIsPermissionsLoading] = useState(true); // État de chargement des permissions
  const [isProfileLoading, setIsProfileLoading] = useState(true);
  const [isTokensLoading, setIsTokensLoading] = useState(true);
  const [twoFactorMethods, setTwoFactorMethods] = useState<TwoFactorMethod[]>([]);
  const [isProfileIncompleteModalOpen, setIsProfileIncompleteModalOpen] = useState(false);
  const [twoFactorEmail, setTwoFactorEmail] = useState<string | null>(null);
  const [, setTwoFactorPassword] = useState<string | null>(null);
  const [twoFactorRememberMe, setTwoFactorRememberMe] = useState<boolean>(false);

  const checkProfileCompletion = (profileData: Profile) => {
    // Applicants register without a postal address; it is only required once the
    // application is accepted (KYC / payment). Never interrupt the /apply funnel either.
    const applicationPending =
      profileData.fundedApplicationStatus === 'DRAFT' ||
      profileData.fundedApplicationStatus === 'SUBMITTED' ||
      profileData.fundedApplicationStatus === 'REJECTED';
    const onApplyPage = window.location.pathname.startsWith('/apply');
    if (!profileData.profileCompleted && !applicationPending && !onApplyPage) {
      setIsProfileIncompleteModalOpen(true);
    }
  };

  /**
   * Refetches the user profile data from the API and updates the global state.
   * This ensures components like ProfileMenu stay in sync with KYC changes.
   */
  const refreshProfile = useCallback(async () => {
    // We access current state values. Since tokens and user are updated via useState,
    // we use the ones currently in scope.
    const storedAccessToken = tokens?.accessToken || tokenStorage.getAccessToken();
    const userId = user?.userId;

    if (storedAccessToken && userId) {
      try {
        const profileData = await fetchProfile(storedAccessToken, userId);
        setProfile(profileData);
      } catch (error) {
        console.error('AuthContext: Failed to refresh profile', error);
      }
    }
  }, [tokens, user]);

  useEffect(() => {
    const storedAccessToken = tokenStorage.getAccessToken();
    const storedRefreshToken = tokenStorage.getRefreshToken();

    if (storedAccessToken && storedRefreshToken) {
      const decoded = decodeJWT(storedAccessToken);

      if (decoded) {
        setTokens({
          accessToken: storedAccessToken,
          refreshToken: storedRefreshToken,
        });
        setUser(decoded);

        // Récupérer le profil utilisateur
        fetchProfile(storedAccessToken, decoded.userId)
          .then((profileData) => {
            setProfile(profileData);
            checkProfileCompletion(profileData);
          })
          .catch((error) =>
            console.error('Failed to fetch sidebar profile', error),
          )
          .finally(() => setIsProfileLoading(false));

        // Récupérer les permissions utilisateur
        setIsPermissionsLoading(true); // Commencer le chargement des permissions
        getUserPermissions(storedAccessToken)
          .then((permissionsData) => {
            if (Array.isArray(permissionsData)) {
              setPermissions(permissionsData);
            } else {
              console.warn('Unexpected permissions format:', permissionsData);
              setPermissions([]);
            }
          })
          .catch((error) => {
            console.error('Failed to fetch user permissions', error);
            setPermissions([]); // Si erreur, mettre un tableau vide
          })
          .finally(() => {
            setIsPermissionsLoading(false); // Le chargement des permissions est terminé
          });
      } else {
        // Invalid token format - clear storage
        console.warn('Invalid JWT token found in storage, clearing...');
        tokenStorage.clearTokens();
        setIsPermissionsLoading(false);
        setIsProfileLoading(false);
      }
    } else {
      setIsPermissionsLoading(false); // Aucun token trouvé, donc on arrête le chargement
      setIsProfileLoading(false);
    }

    setIsTokensLoading(false);
  }, []);

  /**
     * Main login function handling both direct login and 2FA requirements.
     * Now handles 200 OK responses that contain 2FA methods.
     */
  const login = async (email: string, password: string, remember: boolean, method?: TwoFactorMethod, code?: string) => {
    try {
      // loginUser returns the CustomApiResponse structure
      const response = await loginUser(email, password, remember, method, code);

      /**
       * CHECK FOR 2FA REQUIREMENT (Status 200 scenario)
       * If the backend returns success but provides an array (methods) instead of an object (tokens),
       * it means 2FA is enabled for this user.
       */
      if (response.status === 'success' && Array.isArray(response.data)) {
        setTwoFactorMethods(response.data);
        setTwoFactorEmail(email);
        setTwoFactorPassword(password);
        setTwoFactorRememberMe(remember);

        // We throw a specific error string so the hook knows to trigger the 2FA UI
        throw new Error('TWO_FACTOR_REQUIRED');
      }

      /**
       * FULL LOGIN SUCCESS
       * If we reach here, response.data contains the tokens (LoginResponse) and user info.
       */
      const { tokens, user } = response.data;

      // ---------------------------------------------------------------------------
      // CRITICAL FIX: Persist tokens BEFORE making any other API calls.
      // This ensures that Axios interceptors can find the token in storage
      // immediately for subsequent requests (profile, permissions).
      // ---------------------------------------------------------------------------
      tokenStorage.saveTokens(tokens.accessToken, tokens.refreshToken, remember);

      // Update React State
      setTokens(tokens);
      setUser(user);

      // Fetch user profile data (now safe because token is in storage)
      try {
        const profileData = await fetchProfile(tokens.accessToken, user.userId);
        setProfile(profileData);
        checkProfileCompletion(profileData);
      } catch (profileError) {
        console.warn('AuthContext: Failed to fetch profile after login', profileError);
      }

      trackLogin('email');

      // Fetch permissions
      setIsPermissionsLoading(true);
      try {
        const permissionsData = await getUserPermissions(tokens.accessToken);
        setPermissions(Array.isArray(permissionsData) ? permissionsData : []);
      } catch (permError) {
        console.warn('AuthContext: Failed to fetch permissions', permError);
        setPermissions([]);
      } finally {
        setIsPermissionsLoading(false);
      }

      // Clear any previous 2FA state
      clearTwoFactorState();

    } catch (error: any) {
      // If it's the specific 2FA signal, re-throw it for the hook to catch
      if (error.message === 'TWO_FACTOR_REQUIRED') {
        throw error;
      }

      // Traditional error handling (401, 403, 429, etc.)
      console.error('Login failed:', error);
      throw error;
    }
  };

  const loginWithTokens = async (
    accessToken: string,
    refreshToken: string,
    source: string = 'unknown',
    userEmail?: string,
  ) => {
    try {
      const decoded = decodeJWT(accessToken);
      if (!decoded) throw new Error('Failed to decode the JWT.');

      const newTokens: LoginResponse = {
        accessToken,
        refreshToken,
      };

      // Update local state
      setTokens(newTokens);
      setUser(decoded);

      // Persist tokens
      tokenStorage.saveTokens(accessToken, refreshToken);

      // Fetch profile
      try {
        const profileData = await fetchProfile(accessToken, decoded.userId);
        setProfile(profileData);
        checkProfileCompletion(profileData);
      } catch (err) {
        console.error('Failed to fetch sidebar profile', err);
      }

      // Récupérer les permissions après le login
      setIsPermissionsLoading(true);
      try {
        const permissionsData = await getUserPermissions(accessToken);
        if (Array.isArray(permissionsData)) {
          setPermissions(permissionsData);
        } else {
          console.warn('Unexpected permissions format:', permissionsData);
          setPermissions([]);
        }
      } catch (err) {
        console.error('Failed to fetch user permissions', err);
        setPermissions([]);
      } finally {
        setIsPermissionsLoading(false);
      }

      // Optionally track login source
      try {
        trackLogin(source);
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (_e) {}

      // Clear 2FA state on successful login
      clearTwoFactorState();
    } catch (error: any) {
      // Check if 2FA is required for Google/Rfx login (401 with methods array)
      if (error?.response?.status === 401 && error?.response?.data?.methods) {
        const twoFactorError: any = new Error('TWO_FACTOR_REQUIRED');
        twoFactorError.methods = error.response.data.methods;
        twoFactorError.email = userEmail || error.response.data.email;
        twoFactorError.twoFactorRequired = true;
        twoFactorError.source = source;
        throw twoFactorError;
      }
      throw error;
    }
  };

/**
 * Orchestrates the 2FA verification flow.
 * DRY: Uses the permissions provided by the backend tokens instead of re-fetching.
 */
const verifyTwoFactor = async (method: TwoFactorMethod, code: string) => {
  if (!twoFactorEmail) throw new Error('No pending two-factor authentication');

  try {
    const response = await verifyTwoFactorLogin({
      email: twoFactorEmail,
      method,
      code,
    });

    // 1. Handle Partial success: Do nothing, wait for next user input
    if (response.status === 'success' && !response.data) {
      return; 
    }

    // 2. Handle Full success: Login the user
    if (response.status === 'success' && response.data) {
      const { tokens, user } = response.data;

      // Update basic auth state
      setTokens(tokens);
      setUser(user);
      
      // Update permissions immediately using backend data (Optimization)
      setPermissions(tokens.permissionNames || []);
      setIsPermissionsLoading(false);

      // Persist session
      tokenStorage.saveTokens(tokens.accessToken, tokens.refreshToken, twoFactorRememberMe);

      // Trigger side effects
      trackLogin('email');
      
      // Fetch profile (still needed as it contains non-auth UI data)
      try {
        const profileData = await fetchProfile(tokens.accessToken, user.userId);
        setProfile(profileData);
      } catch (err) {
        console.error('Failed to fetch profile after 2FA', err);
      }

      clearTwoFactorState();
    }
  } catch (error: any) {
    /**
     * Error handling: 
     * If 429 (Too Many Requests), the error message from the backend is used.
     */
    const message = error?.response?.data?.error || error.message;
    throw new Error(message);
  }
};

  const clearTwoFactorState = () => {
    setTwoFactorMethods([]);
    setTwoFactorPassword(null);
    setTwoFactorEmail(null);
    setTwoFactorRememberMe(false);
  };

  const setGoogleTwoFactorRequired = (email: string, methods: TwoFactorMethod[]) => {
    setTwoFactorEmail(email);
    setTwoFactorMethods(methods);
    setTwoFactorRememberMe(true); // Google login uses localStorage by default
  };

  const logout = () => {
    tokenStorage.clearTokens();
    setTokens(null);
    setUser(null);
    setProfile(null);
    setPermissions([]); // Réinitialiser les permissions à un tableau vide
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        tokens,
        profile,
        permissions, // Permissions sous forme de tableau
        isPermissionsLoading, // Ajouter l'état de chargement des permissions
        isProfileLoading,
        login,
        loginWithTokens,
        verifyTwoFactor,
        setGoogleTwoFactorRequired,
        isTokensLoading,
        logout,
        refreshProfile, // Expose refreshProfile to all consumers
        twoFactorMethods, // handle 2fa requirement
        twoFactorEmail,
        twoFactorRememberMe,
        clearTwoFactorState,
      }}
    >
      {children}
      {/* The funded application funnel collects its own data: the completion modal may have been
          armed on /login or /register before the redirect to /apply, so it is also gated at render time. */}
      <ProfileIncompleteModal
        open={isProfileIncompleteModalOpen && !location.pathname.startsWith('/apply')}
        onClose={() => setIsProfileIncompleteModalOpen(false)}
      />
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider.');
  }
  return context;
};
